// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a dialog component for editing existing locations.
 * It handles both regular and transient locations with appropriate field
 * restrictions and validation, including address management through Google
 * Places integration and inventory status changes with confirmation workflow.
 */
import { SparseOrganization } from '@/account/api/types';
import { hasUniqueViolationError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormAddressSearch } from '@/components/form-address-search';
import { FormCompanySearch } from '@/components/form-company-search';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  ArticleOutlined,
  TuneOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Location } from '@/location/api/types';
import {
  UpdateLocationParameters,
  updateLocation,
} from '@/location/api/update-location';
import { LocationInventoryToggle } from '@/location/components/location-inventory-toggle';
import { LocationTransientToggle } from '@/location/components/location-transient-toggle';
import {
  locationComponentsSchema,
  sparseCustomerSchema,
} from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating location names.
 * Ensures non-empty string values with appropriate error messaging.
 */
const nameSchema = z
  .string({ required_error: 'Name is required' })
  .min(1, 'Name is required');

/**
 * Schema for validating location addresses.
 * Supports both full addresses and locations without addresses.
 * When an address is provided, ensures all required fields are present.
 */
const addressSchema = z
  .object({
    placeId: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    address: locationComponentsSchema,
  })
  .nullable()
  .refine(
    (value) => value === null || (value.address.formattedAddress ?? '') !== '',
    { message: 'Address is required' }
  );

/**
 * Schema for the complete form data.
 * Combines name, address, inventory status, and company validation.
 * Requires a company to be selected (either organization or customer).
 */
const formSchema = z.object({
  name: nameSchema,
  address: addressSchema,
  isInventory: z.boolean(),
  isTransient: z.boolean(),
  company: sparseCustomerSchema.nullable().refine((value) => value !== null, {
    message: 'A company must be selected',
  }),
});

type FormData = z.infer<typeof formSchema>;

/**
 * Generates default form values from a location object.
 * Handles both locations with and without addresses, ensuring
 * proper type conversion for numeric fields.
 *
 * @param location - The location object to extract defaults from
 * @param organization - The current user's organization
 * @returns Default form values matching the form schema
 */
function getDefaultValues(
  location: Location,
  currentOrganization: SparseOrganization
): FormData {
  const customer = location.customer
    ? { ...location.customer }
    : {
        id: currentOrganization.id,
        key: currentOrganization.key,
        name: currentOrganization.name,
        isArchived: false,
        isCustomer: false,
        isSupplier: false,
        isManufacturer: true,
      };
  return {
    name: location.name,
    isInventory: location.isInventory,
    isTransient: location.isTransient,
    company: customer,
    address: location.address
      ? {
          address: location.address,
          latitude: location.latitude ? Number(location.latitude) : null,
          longitude: location.longitude ? Number(location.longitude) : null,
          placeId: location.googlePlaceId,
        }
      : null,
  };
}

interface Props {
  /**
   * Whether the dialog is currently visible.
   */
  isOpen: boolean;

  /**
   * The location to be edited.
   * Contains all current location data.
   */
  location: Location;

  /**
   * Callback fired when the dialog should close.
   * Called after successful update or when user cancels.
   */
  onClose: () => void;
}

/**
 * A dialog component for editing existing locations in the system.
 *
 * Features:
 * - Different behavior for regular vs. transient locations
 * - Location name validation with uniqueness check
 * - Optional address input with Google Places integration
 * - Inventory status toggle with confirmation workflow
 * - Form validation and error handling
 * - Automatic query cache invalidation on success
 *
 * Form Behavior:
 * - Transient locations can only edit address
 * - Regular locations can edit name, address, and inventory status
 * - Shows error feedback for validation failures
 * - Handles unique constraint violations gracefully
 * - Prevents submission if no changes are made
 * - Confirms inventory status changes due to reporting impact
 *
 * @example
 * ```tsx
 * <EditLocationDialog
 *   location={locationData}
 *   isOpen={showDialog}
 *   onClose={() => setShowDialog(false)}
 * />
 * ```
 */
export function EditLocationDialog(props: Props) {
  const queryClient = useQueryClient();
  const confirmationDialog = useDisclosure();
  const { user } = useCurrentUser();

  /**
   * Mutation for updating location details.
   * Handles success by invalidating location queries and resetting form.
   * Provides specialized error handling for unique constraint violations.
   */
  const updateLocationMutation = useMutation({
    mutationFn: (parameters: UpdateLocationParameters) =>
      updateLocation(props.location.key, props.location.type, parameters),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['location'] });
      reset(getDefaultValues(data.location, user.organization));
      close();
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        if (props.location.isTransient) {
          setError('address', {
            type: 'custom',
            message: 'A transient location with this address already exists',
          });
        } else {
          setError('name', {
            type: 'custom',
            message: 'A location with this name already exists',
          });
        }
      }
    },
  });

  // Initialize form with validation and current location data
  const {
    control,
    formState,
    reset,
    getValues,
    setError,
    handleSubmit,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(props.location, user.organization),
  });
  // Reset form when location data changes to prevent stale data
  // when navigating between locations without unmounting the dialog
  useEffect(() => {
    reset(getDefaultValues(props.location, user.organization));
  }, [props.location, reset, user.organization]);

  const selectedCompany = watch('company');
  // Compare company IDs to detect changes. When location.customer is null,
  // the location belongs to the organization. When selectedCompany matches
  // the organization ID, it also represents the organization (no customer).
  const initialCompanyId = props.location.customer?.id ?? user.organization.id;
  const selectedCompanyId = selectedCompany?.id ?? user.organization.id;
  const hasCompanyChanged = initialCompanyId !== selectedCompanyId;

  const inventoryController = useController({ control, name: 'isInventory' });
  const transientController = useController({ control, name: 'isTransient' });

  // Determine UI states based on mutation and form state
  const isAlertOpen =
    updateLocationMutation.isError &&
    !hasUniqueViolationError(updateLocationMutation.error);

  const isSubmitDisabled = !formState.isDirty;
  const updatedInventoryLabel = inventoryController.field.value
    ? 'inventory'
    : 'non-inventory';

  const previousInventoryLabel = props.location.isInventory
    ? 'inventory'
    : 'non-inventory';

  /**
   * Handles dialog closure.
   * Resets form state and calls onClose callback.
   */
  const close = () => {
    confirmationDialog.close();
    reset();
    props.onClose();
  };

  /**
   * Updates location with modified fields.
   * Only includes fields that have been changed to minimize API payload.
   *
   * @param data - The validated form data
   */
  const update = (data: FormData) => {
    const parameters: UpdateLocationParameters = {};

    if (formState.dirtyFields.name) {
      parameters.name = data.name;
    }

    if (formState.dirtyFields.isInventory) {
      parameters.isInventory = data.isInventory;
    }

    if (formState.dirtyFields.isTransient) {
      parameters.isTransient = data.isTransient;
    }

    if (formState.dirtyFields.address) {
      parameters.address = data.address?.address ?? null;
    }

    if (formState.dirtyFields.company) {
      // Send null if the company is not a customer (i.e., the organization)
      parameters.customerId = data.company ? data.company.id : null;
      if (parameters.customerId === user.organization.id) {
        parameters.customerId = null;
      }
    }

    if (Object.entries(parameters).length > 0) {
      updateLocationMutation.mutate(parameters);
    }
  };

  /**
   * Handles confirmation dialog approval.
   * Proceeds with update using current form values.
   */
  const handleClickConfirm = () => {
    update(getValues());
  };

  /**
   * Handles form submission when validation passes.
   * Shows confirmation dialog for inventory status changes,
   * otherwise proceeds with update.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (formState.dirtyFields.isInventory) {
      return confirmationDialog.open();
    }

    update(data);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while updating the location."
        onClose={updateLocationMutation.reset}
      />
      <Dialog
        open={confirmationDialog.isOpen}
        onClose={confirmationDialog.close}
      >
        <DialogContent dividers>
          <Typography marginBottom={2}>
            Changing the inventory status for <b>{props.location.name}</b> will
            affect historical reporting for this site. Are you sure you want to
            continue?
          </Typography>
          <Alert severity="info">
            If you want to preserve historical reporting for this site as{' '}
            <b>{previousInventoryLabel}</b> and start using{' '}
            <b>{updatedInventoryLabel}</b> treatment only for the future, you
            should create a new <b>{updatedInventoryLabel}</b> site and move
            assets from this site to the new site. Afterwards, you can archive
            this site if you no longer need it.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmationDialog.close}>No, cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleClickConfirm}
          >
            Yes, continue
          </Button>
        </DialogActions>
      </Dialog>
      <DialogTitle>Edit location</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!props.location.isTransient && (
            <Box
              component="fieldset"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                m: 0,
              }}
            >
              <Typography
                component="legend"
                variant="subtitle2"
                sx={{
                  px: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ArticleOutlined fontSize="small" color="action" />
                General
              </Typography>
              <Stack spacing={1.5}>
                <FormCompanySearch
                  name="company"
                  label="Company"
                  control={control}
                />
                {hasCompanyChanged && (
                  <Alert severity="warning">
                    <Typography variant="subtitle2">
                      Changing company for this location
                    </Typography>
                    This will affect reporting for this company and location,
                    but assets at this location will remain assigned to their
                    current projects and owners.
                  </Alert>
                )}
                <FormTextField name="name" label="Name" control={control} />
              </Stack>
            </Box>
          )}
          <FormAddressSearch name="address" label="Address" control={control} />
          <Box
            component="fieldset"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              m: 0,
            }}
          >
            <Typography
              component="legend"
              variant="subtitle2"
              sx={{
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <TuneOutlined fontSize="small" color="action" />
              Options
            </Typography>
            <Stack spacing={1.5}>
              {!props.location.isTransient && (
                <LocationInventoryToggle
                  isInventory={inventoryController.field.value}
                  onChange={inventoryController.field.onChange}
                />
              )}
              <LocationTransientToggle
                isTransient={transientController.field.value}
                onChange={transientController.field.onChange}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
          disabled={isSubmitDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
